import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Search, Package, Info, AlertTriangle, CheckCircle2, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GRCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const poNumberParam = searchParams.get('po');

  const [loading, setLoading] = useState(false);
  const [loadingPO, setLoadingPO] = useState(false);
  const [loadingPOList, setLoadingPOList] = useState(true);
  const [poList, setPoList] = useState([]); // List of POs for dropdown
  const [selectedPO, setSelectedPO] = useState(poNumberParam || '');
  const [poData, setPoData] = useState(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  // GR Header
  // Movement Type options
  const movementTypes = [
    { code: '101', description: 'GR for Purchase Order', category: 'Receipt' },
    { code: '103', description: 'GR into Blocked Stock', category: 'Receipt' },
    { code: '105', description: 'Release from Blocked Stock', category: 'Transfer' },
    { code: '122', description: 'Return to Vendor', category: 'Return' },
    { code: '161', description: 'Returns to Vendor', category: 'Return' },
  ];

  const [formData, setFormData] = useState({
    movement_type: '101',
    posting_date: new Date().toISOString().split('T')[0],
    document_date: new Date().toISOString().split('T')[0],
    delivery_note: '',
    bill_of_lading: '',
    header_text: '',
    created_by: 'ADMIN'
  });

  // GR Items (will be populated from PO)
  const [grItems, setGrItems] = useState([]);

  // Load PO list on mount
  useEffect(() => {
    loadPOList();
  }, []);

  // Load selected PO data when selectedPO changes
  useEffect(() => {
    if (selectedPO) {
      loadPOData(selectedPO);
    }
  }, [selectedPO]);

  // Load list of POs for dropdown (only OPEN and PARTIALLY_RECEIVED)
  const loadPOList = async () => {
    try {
      setLoadingPOList(true);
      const response = await axios.get(`${BACKEND_URL}/api/purchase-orders`);
      // Filter POs that can receive goods (OPEN or PARTIALLY_RECEIVED)
      const eligiblePOs = response.data.filter(po => 
        po.status === 'OPEN' || po.status === 'PARTIALLY_RECEIVED'
      );
      setPoList(eligiblePOs);
    } catch (error) {
      console.error('Error loading PO list:', error);
      toast.error('Failed to load Purchase Orders');
    } finally {
      setLoadingPOList(false);
    }
  };

  const loadPOData = async (poNumber) => {
    if (!poNumber) return;
    
    try {
      setLoadingPO(true);
      const response = await axios.get(`${BACKEND_URL}/api/goods-receipts/po/${poNumber}`);
      const po = response.data;
      
      if (po.items_open_for_gr === 0) {
        toast.error('All items in this PO are fully received. No open items for GR.');
        return;
      }
      
      setPoData(po);
      
      // Initialize GR items from PO items (only items with open quantity)
      const items = po.items
        .filter(item => item.open_quantity > 0)
        .map((item, index) => ({
          item_number: (index + 1) * 10,
          po_number: item.po_number,
          po_item: item.po_item,
          material_id: item.material_id,
          material_description: item.material_description,
          plant: item.plant,
          storage_location: item.storage_location || '',
          // Quantities
          po_quantity: item.po_quantity,
          quantity_already_received: item.quantity_received,
          open_quantity: item.open_quantity,
          quantity: item.open_quantity, // Default to receive full open qty
          unit_of_measure: item.unit_of_measure,
          // Pricing
          net_price: item.net_price,
          price_unit: item.price_unit,
          currency: item.currency,
          // Calculated
          amount: (item.open_quantity * item.net_price) / item.price_unit,
          // Options
          stock_type: '',
          batch_number: '',
          selected: true // By default, select all items
        }));
      
      setGrItems(items);
      setActiveItemIndex(0);
      toast.success(`Loaded PO ${poNumber} with ${items.length} item(s) open for receipt`);
    } catch (error) {
      console.error('Error loading PO:', error);
      toast.error(error.response?.data?.detail || 'Failed to load PO');
      setPoData(null);
      setGrItems([]);
    } finally {
      setLoadingPO(false);
    }
  };

  const handlePOSelect = (poNumber) => {
    setSelectedPO(poNumber);
    if (!poNumber) {
      setPoData(null);
      setGrItems([]);
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-green-100 text-green-800',
      'PARTIALLY_RECEIVED': 'bg-yellow-100 text-yellow-800',
      'FULLY_RECEIVED': 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...grItems];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate amount if quantity changes
    if (field === 'quantity') {
      const qty = parseFloat(value) || 0;
      const price = newItems[index].net_price;
      const priceUnit = newItems[index].price_unit || 1;
      newItems[index].amount = (qty * price) / priceUnit;
    }
    
    setGrItems(newItems);
  };

  const handleItemSelect = (index, selected) => {
    const newItems = [...grItems];
    newItems[index].selected = selected;
    setGrItems(newItems);
  };

  const validateForm = () => {
    if (!poData) {
      toast.error('Please select a Purchase Order');
      return false;
    }
    
    const selectedItems = grItems.filter(item => item.selected);
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item to receive');
      return false;
    }
    
    for (const item of selectedItems) {
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        toast.error(`Item ${item.item_number}: Receipt quantity must be greater than 0`);
        return false;
      }
      
      const qty = parseFloat(item.quantity);
      const openQty = item.open_quantity;
      const tolerance = item.po_quantity * 0.10; // 10% tolerance
      
      if (qty > (openQty + tolerance)) {
        toast.error(`Item ${item.item_number}: Receipt qty (${qty}) exceeds open qty (${openQty}) + 10% tolerance`);
        return false;
      }
      
      if (!item.storage_location) {
        toast.error(`Item ${item.item_number}: Storage Location is required`);
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      const selectedItems = grItems.filter(item => item.selected);
      
      const payload = {
        ...formData,
        po_number: poData.po_number,
        items: selectedItems.map((item, index) => ({
          item_number: (index + 1) * 10,
          material_id: item.material_id,
          material_description: item.material_description,
          plant: item.plant,
          storage_location: item.storage_location,
          po_number: item.po_number,
          po_item: item.po_item,
          quantity: parseFloat(item.quantity),
          unit_of_measure: item.unit_of_measure,
          stock_type: item.stock_type || '',
          batch_number: item.batch_number || null
        }))
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/goods-receipts`, payload);
      toast.success(`Goods Receipt ${response.data.material_document} posted successfully!`);
      navigate(`/p2p/gr-view/${response.data.material_document}/${response.data.material_document_year}`);
    } catch (error) {
      console.error('Error posting GR:', error);
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        toast.error(`Validation Error: ${errorDetail.map(e => e.msg).join(', ')}`);
      } else {
        toast.error('Failed to post Goods Receipt');
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const selectedItems = grItems.filter(item => item.selected);
  const totalQuantity = selectedItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const totalAmount = selectedItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  const currentItem = grItems[activeItemIndex];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/p2p/gr-list')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Post Goods Receipt</h1>
              <p className="text-sm text-gray-500">MIGO - Movement Type 101</p>
            </div>
          </div>
        </div>

        {/* PO Selection Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Purchase Order Reference</h2>
          <div className="flex gap-4 items-end">
            <div className="flex-1 max-w-lg">
              <Label>Select Purchase Order *</Label>
              <div className="relative">
                <select
                  value={selectedPO}
                  onChange={(e) => handlePOSelect(e.target.value)}
                  disabled={loadingPOList || loadingPO}
                  className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm appearance-none bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed pr-10"
                >
                  <option value="">-- Select a Purchase Order --</option>
                  {poList.map((po) => (
                    <option key={po.po_number} value={po.po_number}>
                      {po.po_number} | {po.vendor_name || po.vendor_id} | {po.status} | {po.items?.length || 0} items | {po.currency} {(po.total_value || 0).toLocaleString()}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-0.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {loadingPOList && <p className="text-xs text-gray-500 mt-1">Loading Purchase Orders...</p>}
            </div>
            {loadingPO && (
              <div className="flex items-center text-orange-600">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600 mr-2"></div>
                Loading PO Details...
              </div>
            )}
          </div>
          
          {/* PO Status Legend */}
          {poList.length > 0 && !selectedPO && (
            <div className="mt-4 flex items-center gap-4 text-xs">
              <span className="text-gray-500">Status Legend:</span>
              <span className={`px-2 py-1 rounded ${getStatusColor('OPEN')}`}>OPEN - Ready for GR</span>
              <span className={`px-2 py-1 rounded ${getStatusColor('PARTIALLY_RECEIVED')}`}>PARTIALLY_RECEIVED - Partial GR Posted</span>
            </div>
          )}
          
          {/* No POs message */}
          {!loadingPOList && poList.length === 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                <AlertTriangle className="inline w-4 h-4 mr-1" />
                No Purchase Orders available for Goods Receipt. Only POs with status OPEN or PARTIALLY_RECEIVED can receive goods.
              </p>
            </div>
          )}
        </div>

        {/* PO Header Info (if loaded) */}
        {poData && (
          <form onSubmit={handleSubmit}>
            {/* Organizational Data from PO */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                <Package className="inline w-5 h-5 mr-2" />
                PO: {poData.po_number} - {poData.vendor_name}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-gray-500">Vendor</Label>
                  <p className="font-medium">{poData.vendor_id} - {poData.vendor_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Company Code</Label>
                  <p className="font-medium">{poData.company_code} - {poData.company_code_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Purchasing Org</Label>
                  <p className="font-medium">{poData.purchasing_org}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Currency</Label>
                  <p className="font-medium">{poData.currency}</p>
                </div>
              </div>
              
              {/* Status Badge */}
              <div className="mt-4 flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                  PO Status: {poData.status}
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {poData.items_open_for_gr} of {poData.total_items} items open for receipt
                </span>
              </div>
            </div>

            {/* GR Header Fields */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">GR Header Data</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Movement Type Dropdown */}
                <div className="md:col-span-2">
                  <Label>Movement Type *</Label>
                  <div className="relative">
                    <select
                      value={formData.movement_type}
                      onChange={(e) => setFormData({ ...formData, movement_type: e.target.value })}
                      className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm appearance-none bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-10"
                    >
                      {movementTypes.map((mt) => (
                        <option key={mt.code} value={mt.code}>
                          {mt.code} - {mt.description} ({mt.category})
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-0.5 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {movementTypes.find(mt => mt.code === formData.movement_type)?.description}
                  </p>
                </div>
                <div>
                  <Label>Posting Date *</Label>
                  <Input
                    type="date"
                    value={formData.posting_date}
                    onChange={(e) => setFormData({ ...formData, posting_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Document Date *</Label>
                  <Input
                    type="date"
                    value={formData.document_date}
                    onChange={(e) => setFormData({ ...formData, document_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Delivery Note</Label>
                  <Input
                    value={formData.delivery_note}
                    onChange={(e) => setFormData({ ...formData, delivery_note: e.target.value })}
                    placeholder="Vendor delivery note #"
                    maxLength={16}
                  />
                </div>
                <div>
                  <Label>Bill of Lading</Label>
                  <Input
                    value={formData.bill_of_lading}
                    onChange={(e) => setFormData({ ...formData, bill_of_lading: e.target.value })}
                    placeholder="BOL number"
                    maxLength={16}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Header Text</Label>
                  <Input
                    value={formData.header_text}
                    onChange={(e) => setFormData({ ...formData, header_text: e.target.value })}
                    placeholder="GR header text/notes"
                    maxLength={200}
                  />
                </div>
              </div>
            </div>

            {/* GR Items Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">GR Line Items</h2>
                <div className="text-sm text-gray-500">
                  {selectedItems.length} of {grItems.length} items selected for receipt
                </div>
              </div>

              {/* Item Selector - Dropdown + Buttons */}
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                {/* Dropdown for Item Selection */}
                <div className="flex-1 max-w-md">
                  <Label className="text-sm text-gray-600">Select Item to Edit</Label>
                  <div className="relative">
                    <select
                      value={activeItemIndex}
                      onChange={(e) => setActiveItemIndex(parseInt(e.target.value))}
                      className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm appearance-none bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-10"
                    >
                      {grItems.map((item, index) => (
                        <option key={index} value={index}>
                          Item {item.po_item} | {item.material_id} - {item.material_description} | Open: {item.open_quantity} {item.unit_of_measure} {item.selected ? '✓ Selected' : '○ Not Selected'}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-0.5 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                
                {/* Quick Item Buttons */}
                <div className="flex items-end gap-2 flex-wrap">
                  {grItems.map((item, index) => (
                    <Button
                      key={index}
                      type="button"
                      variant={activeItemIndex === index ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveItemIndex(index)}
                      className={`${!item.selected ? "opacity-50" : ""} ${activeItemIndex === index ? "ring-2 ring-orange-500" : ""}`}
                      title={`${item.material_id} - ${item.material_description}`}
                    >
                      {item.po_item}
                      {item.selected && <CheckCircle2 className="w-3 h-3 ml-1 text-green-500" />}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Quick Select/Deselect All */}
              <div className="flex items-center gap-4 mb-4 pb-4 border-b">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newItems = grItems.map(item => ({ ...item, selected: true }));
                    setGrItems(newItems);
                  }}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Select All Items
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newItems = grItems.map(item => ({ ...item, selected: false }));
                    setGrItems(newItems);
                  }}
                >
                  Deselect All
                </Button>
              </div>

              {/* Current Item Details */}
              {currentItem && (
                <div className="border-t pt-4">
                  {/* Item Selection */}
                  <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-50 rounded-md">
                    <Checkbox
                      checked={currentItem.selected}
                      onCheckedChange={(checked) => handleItemSelect(activeItemIndex, checked)}
                    />
                    <Label className="text-sm font-medium">
                      Receive this item (PO Item {currentItem.po_item})
                    </Label>
                  </div>

                  {/* Material Info */}
                  <div className="bg-orange-50 p-3 rounded-md mb-4">
                    <p className="font-medium text-orange-900">
                      {currentItem.material_id} - {currentItem.material_description}
                    </p>
                    <p className="text-sm text-orange-700">
                      Plant: {currentItem.plant} | UOM: {currentItem.unit_of_measure}
                    </p>
                  </div>

                  {/* Quantities Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div>
                      <Label className="text-gray-500">PO Quantity</Label>
                      <Input value={currentItem.po_quantity} disabled className="bg-gray-100 font-medium" />
                    </div>
                    <div>
                      <Label className="text-gray-500">Already Received</Label>
                      <Input value={currentItem.quantity_already_received} disabled className="bg-gray-100" />
                    </div>
                    <div>
                      <Label className="text-gray-500">Open Quantity</Label>
                      <Input 
                        value={currentItem.open_quantity} 
                        disabled 
                        className={`font-medium ${currentItem.open_quantity > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-100'}`}
                      />
                    </div>
                    <div>
                      <Label className="text-orange-700 font-semibold">Receipt Qty *</Label>
                      <Input
                        type="number"
                        step="0.001"
                        value={currentItem.quantity}
                        onChange={(e) => handleItemChange(activeItemIndex, 'quantity', e.target.value)}
                        disabled={!currentItem.selected}
                        className="border-orange-300 focus:border-orange-500"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-gray-500">UOM</Label>
                      <Input value={currentItem.unit_of_measure} disabled className="bg-gray-100" />
                    </div>
                  </div>

                  {/* Over-receipt Warning */}
                  {currentItem.quantity > currentItem.open_quantity && (
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md mb-4 flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">Over-Receipt Warning</p>
                        <p className="text-sm text-yellow-700">
                          Receipt qty ({currentItem.quantity}) exceeds open qty ({currentItem.open_quantity}). 
                          Max allowed with 10% tolerance: {(currentItem.open_quantity + currentItem.po_quantity * 0.1).toFixed(3)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Location & Price */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <Label>Storage Location *</Label>
                      <Input
                        value={currentItem.storage_location}
                        onChange={(e) => handleItemChange(activeItemIndex, 'storage_location', e.target.value)}
                        disabled={!currentItem.selected}
                        required
                      />
                    </div>
                    <div>
                      <Label>Stock Type</Label>
                      <select
                        value={currentItem.stock_type}
                        onChange={(e) => handleItemChange(activeItemIndex, 'stock_type', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        disabled={!currentItem.selected}
                      >
                        <option value="">Unrestricted</option>
                        <option value="Q">Quality Inspection</option>
                        <option value="S">Blocked Stock</option>
                      </select>
                    </div>
                    <div>
                      <Label>Batch Number</Label>
                      <Input
                        value={currentItem.batch_number}
                        onChange={(e) => handleItemChange(activeItemIndex, 'batch_number', e.target.value)}
                        disabled={!currentItem.selected}
                        placeholder="Optional"
                        maxLength={10}
                      />
                    </div>
                    <div>
                      <Label className="text-gray-500">Net Price</Label>
                      <Input 
                        value={`${currentItem.net_price} / ${currentItem.price_unit} ${currentItem.unit_of_measure}`} 
                        disabled 
                        className="bg-gray-100" 
                      />
                    </div>
                  </div>

                  {/* Item Value */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2"></div>
                    <div>
                      <Label className="text-gray-500">Item Amount</Label>
                      <Input
                        value={`${currentItem.currency} ${currentItem.amount?.toFixed(2) || '0.00'}`}
                        disabled
                        className="bg-gray-100 font-medium text-right"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Totals Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">GR Totals</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-orange-50 p-4 rounded-lg">
                  <Label className="text-orange-700">Total Items</Label>
                  <p className="text-2xl font-bold text-orange-900">{selectedItems.length}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <Label className="text-green-700">Total Quantity</Label>
                  <p className="text-2xl font-bold text-green-900">{totalQuantity.toFixed(3)}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <Label className="text-orange-700">Total Value</Label>
                  <p className="text-2xl font-bold text-orange-900">{poData?.currency} {totalAmount.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={() => navigate('/p2p/gr-list')}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || selectedItems.length === 0}>
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Posting...' : 'Post Goods Receipt'}
              </Button>
            </div>
          </form>
        )}

        {/* Empty State */}
        {!poData && !loadingPO && (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Purchase Order Selected</h3>
            <p className="text-gray-500 mb-4">Enter a PO number above and click &quot;Load PO&quot; to begin goods receipt</p>
            <div className="flex items-start gap-2 text-left max-w-md mx-auto bg-orange-50 p-4 rounded-lg">
              <Info className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-orange-800">
                <p className="font-medium mb-1">How Goods Receipt works:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Search for a Purchase Order with open items</li>
                  <li>Select items and enter received quantities</li>
                  <li>Post GR to update inventory and PO status</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GRCreate;
